import React, { useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
  FormHelperText,
  useTheme,
  Stack,
  Avatar,
  IconButton
} from '@mui/material';
import { useFormikContext } from 'formik';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Button } from '../../../../shared/components/ui/Button';
import { GENDER_OPTIONS, MARITAL_STATUS_OPTIONS, STATUS_OPTIONS } from '../../constants/residentConstants';
import type { ResidentFormData } from '../../types/resident.types';
import { useQuery } from '@tanstack/react-query';
import { sectorService } from '../../../../shared/services/sector/sectorService';
import { unitService } from '../../../../shared/services/unit/unitService';
import { useAuth } from '../../../auth';

type ResidentFormProps = {
  mode: 'create' | 'edit' | 'view';
  loading?: boolean;
  onCancel?: () => void;
};

export function ResidentForm({
  mode,
  loading = false,
  onCancel,
}: ResidentFormProps) {
  const theme = useTheme();
  const auth = useAuth();
  const { values, errors, touched, handleChange, setFieldValue, isValid } = useFormikContext<ResidentFormData>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isView = mode === 'view';

  // Buscar setores (blocos) pelo tenantId
  const { data: sectors = [], isLoading: loadingSectors } = useQuery({
    queryKey: ['sectors', auth.selectedTenant],
    queryFn: async () => {
      const res = await sectorService.list();
      return res.data;
    },
  });

  // Buscar unidades (apartamentos) pelo setor selecionado
  const { data: units = [], isLoading: loadingUnits } = useQuery({
    queryKey: ['units', values.sector_id],
    queryFn: async () => {
      if (!values.sector_id) return [];
      const res = await unitService.list({ sectorId: values.sector_id });
      return res.data;
    },
    enabled: !!values.sector_id,
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFieldValue('avatarFile', file);

      // Criar preview da imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        setFieldValue('imagePreview', e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarClick = () => {
    if (!isView && !loading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
      <Typography variant="h6" gutterBottom color={theme.palette.text.primary}>
        Dados Pessoais
      </Typography>
      <Divider sx={{ mb: 2, borderColor: theme.palette.divider }} />

      <Grid container spacing={3}>
        {/* Campo de Avatar */}
        <Grid item xs={12} md={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              src={values.imagePreview || values.imageUrl}
              sx={{
                width: 80,
                height: 80,
                cursor: isView || loading ? 'default' : 'pointer',
                border: `2px solid ${theme.palette.divider}`,
                '&:hover': !isView && !loading ? {
                  opacity: 0.8,
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease-in-out'
                } : {}
              }}
              onClick={handleAvatarClick}
            >
              {!values.imagePreview && !values.imageUrl && (
                <PhotoCameraIcon sx={{ fontSize: 32, color: theme.palette.text.secondary }} />
              )}
            </Avatar>

            <Box>
              <Typography variant="body1" color={theme.palette.text.primary} fontWeight={500}>
                Foto do Residente
              </Typography>
              <Typography variant="body2" color={theme.palette.text.secondary} sx={{ mb: 1 }}>
                Clique na imagem para alterar
              </Typography>
              {!isView && !loading && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAvatarClick}
                  startIcon={<PhotoCameraIcon />}
                >
                  Selecionar Foto
                </Button>
              )}
            </Box>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={isView || loading}
          />

          {touched.avatarFile && errors.avatarFile && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {errors.avatarFile}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nome"
            name="name"
            value={values.name}
            onChange={handleChange}
            error={touched.name && Boolean(errors.name)}
            helperText={touched.name && errors.name}
            disabled={isView || loading}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.primary,
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="E-mail"
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email && errors.email}
            disabled={isView || loading}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.primary,
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="CPF"
            name="documentNumber"
            value={values.documentNumber}
            onChange={handleChange}
            error={touched.documentNumber && Boolean(errors.documentNumber)}
            helperText={touched.documentNumber && errors.documentNumber}
            disabled={isView || loading}
            placeholder="000.000.000-00"
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.primary,
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Telefone"
            name="phoneNumber"
            value={values.phoneNumber}
            onChange={handleChange}
            error={touched.phoneNumber && Boolean(errors.phoneNumber)}
            helperText={touched.phoneNumber && errors.phoneNumber}
            disabled={isView || loading}
            placeholder="(11) 99999-9999"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.primary,
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Data de Nascimento"
            name="birth_date"
            type="date"
            value={values.birth_date}
            onChange={handleChange}
            error={touched.birth_date && Boolean(errors.birth_date)}
            helperText={touched.birth_date && errors.birth_date}
            disabled={isView || loading}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.primary,
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            error={touched.gender_flag && Boolean(errors.gender_flag)}
            disabled={isView || loading}
          >
            <InputLabel sx={{ color: theme.palette.text.secondary }}>Gênero *</InputLabel>
            <Select
              name="gender_flag"
              value={values.gender_flag}
              onChange={handleChange}
              label="Gênero *"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiSelect-select': {
                  color: theme.palette.text.primary,
                }
              }}
            >
              {GENDER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {touched.gender_flag && errors.gender_flag && (
              <FormHelperText>{errors.gender_flag}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            error={touched.marital_flag && Boolean(errors.marital_flag)}
            disabled={isView || loading}
          >
            <InputLabel sx={{ color: theme.palette.text.secondary }}>Estado Civil *</InputLabel>
            <Select
              name="marital_flag"
              value={values.marital_flag}
              onChange={handleChange}
              label="Estado Civil *"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiSelect-select': {
                  color: theme.palette.text.primary,
                }
              }}
            >
              {MARITAL_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {touched.marital_flag && errors.marital_flag && (
              <FormHelperText>{errors.marital_flag}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            error={touched.status && Boolean(errors.status)}
            disabled={isView || loading}
          >
            <InputLabel sx={{ color: theme.palette.text.secondary }}>Status *</InputLabel>
            <Select
              name="status"
              value={values.status}
              onChange={handleChange}
              label="Status *"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiSelect-select': {
                  color: theme.palette.text.primary,
                }
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {touched.status && errors.status && (
              <FormHelperText>{errors.status}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nacionalidade"
            name="nationality"
            value={values.nationality}
            onChange={handleChange}
            error={touched.nationality && Boolean(errors.nationality)}
            helperText={touched.nationality && errors.nationality}
            disabled={isView || loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.primary,
              }
            }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }} color={theme.palette.text.primary}>
        CNH (Opcional)
      </Typography>
      <Divider sx={{ mb: 2, borderColor: theme.palette.divider }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Número da CNH"
            name="cnh_number"
            value={values.cnh_number}
            onChange={handleChange}
            error={touched.cnh_number && Boolean(errors.cnh_number)}
            helperText={touched.cnh_number && errors.cnh_number}
            disabled={isView || loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.primary,
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Vencimento da CNH"
            name="cnh_expiration"
            type="date"
            value={values.cnh_expiration}
            onChange={handleChange}
            error={touched.cnh_expiration && Boolean(errors.cnh_expiration)}
            helperText={touched.cnh_expiration && errors.cnh_expiration}
            disabled={isView || loading}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.primary,
              }
            }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }} color={theme.palette.text.primary}>
        Dados da Unidade
      </Typography>
      <Divider sx={{ mb: 2, borderColor: theme.palette.divider }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            error={touched.sector_id && Boolean(errors.sector_id)}
            disabled={isView || loading || loadingSectors}
          >
            <InputLabel sx={{ color: theme.palette.text.secondary }}>Bloco *</InputLabel>
            <Select
              name="sector_id"
              value={values.sector_id || ''}
              onChange={e => {
             
                handleChange(e);
                setFieldValue('unit_id', ''); // Limpa unidade ao trocar bloco
              }}
              label="Bloco *"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiSelect-select': {
                  color: theme.palette.text.primary,
                }
              }}
            >
              {sectors.map((sector: any) => (
                <MenuItem key={sector.id} value={sector.id}>
                  {sector.name}
                </MenuItem>
              ))}
            </Select>
            {touched.sector_id && errors.sector_id && (
              <FormHelperText>{errors.sector_id}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            error={touched.unit_id && Boolean(errors.unit_id)}
            disabled={isView || loading || loadingUnits || !values.sector_id}
          >
            <InputLabel sx={{ color: theme.palette.text.secondary }}>Apartamento *</InputLabel>
            <Select
              name="unit_id"
              value={values.unit_id || ''}
              onChange={e => {
                handleChange(e);
                // Encontrar a unidade selecionada para acessar seus dados
                const selectedUnit = units.find((unit: any) => unit.id === e.target.value);
                if (selectedUnit) {
                  console.log('Unidade selecionada:', selectedUnit);
                  // Você pode acessar selectedUnit.number, selectedUnit.description, etc.
                  setFieldValue('section_name', selectedUnit.number || ''); // Define section_name com a descrição da unidade
                } else {
                  setFieldValue('section_name', ''); // Limpa section_name se nenhuma unidade selecionada
                }
              }}
              label="Apartamento *"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiSelect-select': {
                  color: theme.palette.text.primary,
                }
              }}
            >
              {units.length === 0 ? (
                <MenuItem disabled value="">
                  Nenhuma unidade registrada para este bloco/seção
                </MenuItem>
              ) : (
                units.map((unit: any) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.number} - {unit.number}
                  </MenuItem>
                ))
              )}
            </Select>
            {touched.unit_id && errors.unit_id && (
              <FormHelperText>{errors.unit_id}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>

      {/* Botões de ação */}
      <Box sx={{ mt: 4 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !isValid}
            loading={loading}
          >
            {mode === 'create' ? 'Criar residente' : 'Salvar alterações'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}